import { Building2Icon, Globe2Icon, SaveIcon } from "lucide-react";
import type { SyntheticEvent } from "react";

import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Field, FieldDescription, FieldError, FieldLabel } from "../../components/ui/field";
import { Input } from "../../components/ui/input";

interface WorkspaceSettingsPanelProps {
  canEditSubdomain: boolean;
  isOnline: boolean;
  nameError: string;
  namePending: boolean;
  subdomain: string;
  subdomainError: string;
  subdomainPending: boolean;
  workspaceName: string;
  onNameChange: (value: string) => void;
  onSaveName: (event: SyntheticEvent<HTMLFormElement>) => void;
  onSaveSubdomain: (event: SyntheticEvent<HTMLFormElement>) => void;
  onSubdomainChange: (value: string) => void;
}

function WorkspaceAddressCard(props: WorkspaceSettingsPanelProps) {
  return (
    <Card className="lg:col-span-2">
      <CardHeader className="border-b">
        <div className="grid size-10 place-items-center rounded-sm border-[1.5px] border-primary bg-info-background text-primary">
          <Globe2Icon aria-hidden="true" className="size-5" />
        </div>
        <CardTitle>Workspace address</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="grid gap-5" onSubmit={props.onSaveSubdomain}>
          <Field data-invalid={Boolean(props.subdomainError)}>
            <FieldLabel htmlFor="workspace-subdomain">Workspace subdomain</FieldLabel>
            <div className="grid gap-2 sm:grid-cols-[minmax(0,24rem)_auto] sm:items-center">
              <Input
                aria-invalid={Boolean(props.subdomainError)}
                autoCapitalize="none"
                disabled={props.subdomainPending || !props.isOnline}
                id="workspace-subdomain"
                maxLength={63}
                readOnly={!props.canEditSubdomain}
                spellCheck={false}
                value={props.subdomain}
                onChange={(event) => {
                  props.onSubdomainChange(event.target.value);
                }}
              />
              <span className="font-mono text-sm text-muted-foreground">.ccbox.ai</span>
            </div>
            <FieldDescription>
              Your team can bookmark{" "}
              <strong className="font-medium text-foreground">
                https://{props.subdomain || "your-workspace"}.ccbox.ai
              </strong>
              . Previous addresses continue to work if the consultant changes this one.
            </FieldDescription>
            <FieldError>{props.subdomainError}</FieldError>
          </Field>
          {props.canEditSubdomain ? (
            <Button
              className="justify-self-start"
              disabled={props.subdomainPending || !props.isOnline}
              type="submit"
            >
              <SaveIcon aria-hidden="true" data-icon="inline-start" /> Save address
            </Button>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

function WorkspaceNameCard(props: WorkspaceSettingsPanelProps) {
  return (
    <Card>
      <CardHeader className="border-b">
        <div className="grid size-10 place-items-center rounded-sm border-[1.5px] border-primary bg-info-background text-primary">
          <Building2Icon aria-hidden="true" className="size-5" />
        </div>
        <CardTitle id="workspace-settings-heading">Workspace name</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="grid gap-5" onSubmit={props.onSaveName}>
          <Field data-invalid={Boolean(props.nameError)}>
            <FieldLabel htmlFor="workspace-name">Workspace name</FieldLabel>
            <Input
              aria-invalid={Boolean(props.nameError)}
              disabled={props.namePending || !props.isOnline}
              id="workspace-name"
              maxLength={160}
              value={props.workspaceName}
              onChange={(event) => {
                props.onNameChange(event.target.value);
              }}
            />
            <FieldDescription>This name appears throughout CCBox.</FieldDescription>
            <FieldError>{props.nameError}</FieldError>
          </Field>
          <Button
            className="justify-self-start"
            disabled={props.namePending || !props.isOnline}
            type="submit"
          >
            <SaveIcon aria-hidden="true" data-icon="inline-start" /> Save name
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export function WorkspaceSettingsPanel(props: WorkspaceSettingsPanelProps) {
  return (
    <section
      aria-labelledby="workspace-settings-heading"
      className="grid gap-5 py-8 lg:grid-cols-2"
    >
      <WorkspaceAddressCard {...props} />
      <WorkspaceNameCard {...props} />
    </section>
  );
}
